<template>
  <teleport v-if="targetElement" :to="targetElement">
    <transition name="net-popup-menu__transition" appear>
      <menu
        class="net-popup-menu"
        ref="el"
        @keydown="onKeydown"
        @wheel="onMouseWheel"
        :style="{
          left: `${viewportCoordinates.x + offset.x + extraOffset.x}px`,
          top: `${viewportCoordinates.y + offset.y + extraOffset.y}px`,
          width: `${props.width}px`,
          height: `${props.height}px`
        }"
        v-bind="$attrs"
      >
        <div class="net-popup-menu__header" v-if="$slots['title'] || closable">
          <div v-if="$slots['title']" class="net-popup-menu__title">
            <slot name="title"></slot>
          </div>
          <button
            v-if="closable"
            type="button"
            @click.prevent.stop="emit('close')"
            class="button-icon ml-auto"
          >
            <lucide-icon name="x" />
          </button>
        </div>
        <div class="net-popup-menu__body">
          <slot></slot>
        </div>
      </menu>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import {
  ref,
  reactive,
  onMounted,
  onBeforeUnmount,
  nextTick,
  computed
} from "vue";
import { watchThrottled } from "@vueuse/core";
import { useCanvasDimensions } from "./useCanvasDimensions";

import LucideIcon from "@core/ui/LucideIcon.vue";
import { onClickOutside } from "@core/composables/onMouseDownOutside";

defineOptions({
  inheritAttrs: false
});
const props = withDefaults(
  defineProps<{
    x?: number;
    y?: number;
    height?: number;
    width?: number;
    offsetX?: number;
    offsetY?: number;
    closable?: boolean;
  }>(),
  {
    x: 0,
    y: 0,
    height: 350,
    width: 308,
    closable: true
  }
);

const emit = defineEmits<{
  (e: "wheel", event: WheelEvent): void;
  (e: "close"): void;
}>();

const el = ref<HTMLElement>();
const { netId, toViewportComputed } = useCanvasDimensions();
const viewportCoordinates = toViewportComputed(() => ({
  x: props.x,
  y: props.y
}));

const offset = computed(() => ({
  x: props.offsetX ?? 0,
  y: props.offsetY ?? -props.height + 2
}));
const extraOffset = reactive({
  x: 0,
  y: 0
});

// Get the net container to teleport the actionbar to
const targetElement = ref<HTMLElement | null>(null);
setTimeout(() => {
  targetElement.value = document.querySelector(`#${netId.value}`);
  nextTick(() => {
    positionInBounds();
  });
}, 1);

function onKeydown(event: KeyboardEvent) {
  if (
    (event.key == "+" || event.key == "-") &&
    (event.ctrlKey || event.metaKey)
  ) {
    event.preventDefault();
    console.log("zoom");
  }
}

function onMouseWheel(event: WheelEvent) {
  if (event.metaKey || event.ctrlKey) {
    event.preventDefault();
  }
  emit("wheel", event);
}

function positionInBounds() {
  if (targetElement.value == null) return;
  const targetRect = targetElement.value.getBoundingClientRect();
  const elRect = getUntransformedBoundingRect(el.value);

  const elBounds = {
    top: elRect.y - extraOffset.y,
    bottom: elRect.y + elRect.height - extraOffset.y,
    left: elRect.x - extraOffset.x,
    right: elRect.x + elRect.width - extraOffset.x
  };

  let x = 0;
  let y = 0;

  if (elBounds.top < targetRect.top) {
    y = targetRect.top - elBounds.top;
  }
  if (elBounds.bottom > targetRect.bottom) {
    y = targetRect.bottom - elBounds.bottom;
  }
  if (elBounds.left < targetRect.left) {
    x = targetRect.left - elBounds.left;
  }
  if (elBounds.right > targetRect.right) {
    x = targetRect.right - elBounds.right;
  }
  extraOffset.x = x;
  extraOffset.y = y;
}

function getUntransformedBoundingRect(element?: HTMLElement) {
  if (!element) return { x: 0, y: 0, width: 0, height: 0 };

  let offsetLeft = 0;
  let offsetTop = 0;
  let el: HTMLElement | null = element;
  const width = element.offsetWidth;
  const height = element.offsetHeight;

  do {
    offsetLeft += el?.offsetLeft || 0;
    offsetTop += el?.offsetTop || 0;

    el = (el?.offsetParent ?? null) as HTMLElement | null;
  } while (el);

  return { x: offsetLeft, y: offsetTop, width, height };
}

watchThrottled(
  [() => viewportCoordinates.value.x, () => viewportCoordinates.value.y],
  () => {
    positionInBounds();
  },
  { throttle: 1, trailing: true, leading: false }
);

let unbindClick: (() => void) | undefined = undefined;

function onShow() {
  setTimeout(() => {
    if (unbindClick) {
      unbindClick();
    }
    unbindClick = onClickOutside(
      [el],
      (event) => {
        const path = event.composedPath();
        if (
          path.some(
            (el) =>
              el instanceof HTMLElement &&
              (el.classList.contains("colorpicker-popout") ||
                el.classList.contains("symbolpicker-popout"))
          )
        ) {
          // Don't close if clicking inside a color picker popout.
          return;
        }
        if (
          event.target instanceof HTMLElement &&
          event.target.classList.contains("net__container")
        ) {
          // This check prevents drag-clicks that started in the popup from closing it.
          return;
        }
        emit("close");
      },
      { capture: false }
    );
  }, 10);
}

onMounted(() => {
  onShow();
});
onBeforeUnmount(() => {
  if (unbindClick) {
    unbindClick();
  }
  emit("close");
});
</script>

<style lang="scss">
.net-popup-menu {
  position: absolute;
  box-sizing: border-box;
  // left: 0;
  // bottom: calc(100% + 0.5em);
  max-height: 100%;

  margin: 0;
  padding: 0;
  list-style: none;

  background-color: var(--color-background-close);
  box-shadow: 0 0.05em 0.25em var(--color-shadow-2);
  pointer-events: all;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: min-content 1fr;

  border-radius: 5px;
  transform-origin: left bottom;
  z-index: 0;

  &__header {
    background-color: var(--color-background);
    display: flex;
    justify-content: end;
    min-height: 2em;
  }
  &__title {
    align-self: center;
    padding-left: 1rem;
    color: var(--color-text-medium);
    font-size: 12px;
    font-weight: 600;
    margin-right: auto;
  }
  &__body {
    position: relative;
    padding: 1rem;
    overflow-x: hidden;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__transition {
    &-enter-active,
    &-leave-active {
      transition:
        opacity 250ms,
        transform 250ms;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: scale(0.9);
    }
  }
}
</style>
